// Shell: navigation, branding and identity all come from the resolved schema. The top bar is two
// rows: the page title with the server clock, live indicator, page refresh and view menu, then the
// dashboard's filters (passed in as `toolbar`) on their own row so neither can squeeze the other.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { Shell } from '../app/types';
import { Icon } from '../ui/Icon';
import { useConnectionStatus } from '../realtime/hooks';
import { useAppDispatch } from '../app/store';
import { logout } from '../app/authSlice';
import { realtime } from '../realtime/client';
import { api } from '../app/api';
import { BrandLogo } from '../ui/BrandLogo';
import { ServerClock } from '../ui/ServerClock';
import { setDisplayTimeZone } from '../lib/format';
import { navEntries } from './nav-groups';

type Theme = 'light' | 'dark' | 'system';
function readTheme(): Theme {
  try {
    return (localStorage.getItem('msld.theme') as Theme) || 'system';
  } catch {
    return 'system';
  }
}

/** Groups the viewer opened or closed, remembered in this browser (a convenience, safe to lose). */
const NAV_GROUPS_KEY = 'msld.nav-groups';
function readOpenGroups(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(NAV_GROUPS_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

function LiveIndicator({ pollSeconds }: { pollSeconds: number }) {
  const conn = useConnectionStatus();
  const cls = conn === 'open' ? '' : conn === 'connecting' ? 'connecting' : 'down';
  const label = conn === 'open' ? 'Live' : conn === 'connecting' ? 'Connecting' : `Offline · refreshing every ${pollSeconds}s`;
  return (
    <span className={`live-pill ${cls}`} role="status" aria-label={label} title={conn === 'open' ? 'Receiving live updates' : label}>
      <span className="dot" aria-hidden />
      <span className="live-text">{label}</span>
    </span>
  );
}

export function AppShell({ shell, title, toolbar, actions, children }: { shell: Shell; title: string; toolbar?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [navOpen, setNavOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(readOpenGroups);
  const toggleGroup = (key: string, open: boolean) =>
    setOpenGroups((g) => {
      const next = { ...g, [key]: open };
      try {
        localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable: the group still toggles for this visit */
      }
      return next;
    });
  const [headerHidden, setHeaderHidden] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const swipeX = useRef<number | null>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const timeZone = shell.syncBoundary?.timeZone ?? 'UTC';
  setDisplayTimeZone(timeZone);
  useEffect(() => {
    setNavOpen(false);
    setHeaderHidden(false);
  }, [location.pathname]);
  // On small screens the header slides away while scrolling down and returns on the way up (the
  // CSS only moves it below 1100px). It stays while a filter inside it has focus or a menu open.
  useEffect(() => {
    let last = window.scrollY;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        if (Math.abs(y - last) < 8) return;
        const header = headerRef.current;
        const busy = !!header && (header.contains(document.activeElement) || !!header.querySelector('.popover'));
        setHeaderHidden(y > last && y > 96 && !busy);
        last = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
  // The page behind the open drawer doesn't scroll.
  useEffect(() => {
    if (!navOpen) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [navOpen]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNavOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('msld.theme', theme);
    } catch {
      /* per-viewer convenience only */
    }
  }, [theme]);
  useEffect(() => {
    document.title = title ? `${title} · ${shell.branding?.productName ?? 'Dashboard'}` : shell.branding?.productName ?? 'Dashboard';
    document.documentElement.dir = shell.localization?.direction ?? 'ltr';
  }, [title, shell]);
  const initials = shell.user.name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const nextTheme: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' };
  let navIndex = 0;
  const themeIcon = theme === 'dark' ? 'moon' : theme === 'light' ? 'sun' : 'monitor';

  return (
    <div className={`shell${navOpen ? ' nav-open' : ''}`}>
      {navOpen && <div className="scrim" onClick={() => setNavOpen(false)} />}
      <aside
        className="sidebar"
        aria-label="Navigation"
        // Swipe left to close the drawer on touch screens.
        onTouchStart={(e) => (swipeX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (navOpen && swipeX.current !== null && e.changedTouches[0].clientX - swipeX.current < -60) setNavOpen(false);
          swipeX.current = null;
        }}
      >
        <div className="brand">
          {shell.branding?.logo ? (
            <BrandLogo logo={shell.branding.logo} />
          ) : (
            <div className="brand-mark" style={{ background: shell.branding?.accent ?? 'var(--accent-solid)' }} aria-hidden>
              {shell.branding?.logoText}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div className="brand-name">{shell.branding?.productName}</div>
            <div className="brand-tenant">{shell.tenant.name}</div>
          </div>
        </div>
        <nav>
          {shell.navigation.sections.map((s) => (
            <div className="nav-section" key={s.label}>
              <div className="nav-label">{s.label}</div>
              {navEntries(s.items).map((e) => {
                const link = (it: (typeof s.items)[number], sub = false) => (
                  <NavLink
                    key={it.id}
                    to={`/d/${it.dashboard}`}
                    onClick={() => setNavOpen(false)}
                    className={({ isActive }) => `nav-item${sub ? ' nav-sub-item' : ''}${isActive ? ' active' : ''}`}
                    style={{ ['--i' as any]: navIndex++ }}
                  >
                    <Icon name={it.icon} size={16} />
                    {it.label}
                  </NavLink>
                );
                if (e.kind === 'item') return link(e.item);
                // A group opens by itself while one of its pages is showing; otherwise as last left.
                const key = `${s.label}/${e.label}`;
                const current = e.items.some((it) => location.pathname === `/d/${it.dashboard}`);
                const open = openGroups[key] ?? current;
                const id = `nav-group-${key.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
                return (
                  <div className="nav-group" key={key}>
                    <button
                      type="button"
                      className={`nav-item nav-group-head${current && !open ? ' has-active' : ''}`}
                      aria-expanded={open}
                      aria-controls={id}
                      onClick={() => toggleGroup(key, !open)}
                      style={{ ['--i' as any]: navIndex++ }}
                    >
                      <Icon name={e.icon} size={16} />
                      {e.label}
                      <span className="nav-chevron" aria-hidden>
                        <Icon name="chevronDown" size={14} />
                      </span>
                    </button>
                    {open && (
                      <div className="nav-sub" id={id} role="group" aria-label={e.label}>
                        {e.items.map((it) => link(it, true))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="user-chip">
            <div className="avatar" aria-hidden>
              {initials}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shell.user.name}</div>
              <div className="muted" style={{ fontSize: 12 }}>{shell.user.roleLabel}</div>
            </div>
            <button className="icon-btn" title={`Theme: ${theme}`} aria-label={`Theme: ${theme}. Switch theme`} onClick={() => setTheme(nextTheme[theme])}>
              <Icon name={themeIcon} />
            </button>
            <button
              className="icon-btn"
              title="Sign out"
              aria-label="Sign out"
              onClick={() => {
                realtime.disconnect();
                dispatch(logout());
                dispatch(api.util.resetApiState());
              }}
            >
              <Icon name="logout" />
            </button>
          </div>
        </div>
      </aside>
      <div className="main">
        <header className={`topbar${headerHidden && !navOpen ? ' is-hidden' : ''}`} ref={headerRef}>
          <div className="topbar-row">
            <button className="icon-btn menu-btn" aria-label="Open navigation" aria-expanded={navOpen} onClick={() => setNavOpen(true)}>
              <Icon name="menu" />
            </button>
            {shell.branding?.logo && <BrandLogo logo={shell.branding.logo} variant="emblem" className="topbar-logo" />}
            <h1 title={title}>{title}</h1>
            {/* On phones this wraps onto its own line under the title. */}
            <div className="topbar-status">
              <ServerClock timeZone={timeZone} />
              <LiveIndicator pollSeconds={shell.realtime?.fallbackPollSeconds ?? 30} />
            </div>
            <div className="topbar-end">
              {/* A full browser reload: recovers a page that is stuck, not just its data. */}
              <button className="btn refresh-btn" onClick={() => window.location.reload()} title="Reload the whole page" aria-label="Refresh page">
                <Icon name="reload" size={15} />
                <span className="refresh-label">Refresh</span>
              </button>
              {actions}
            </div>
          </div>
          {toolbar && <div className="topbar-tools">{toolbar}</div>}
        </header>
        <main className="content">{children}</main>
      </div>
    </div>
  );
}
